import React, { useContext } from "react";
import { PressEvent } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { useHistory } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE, SUPPORT_EMAIL } from "common/constants";
import { buildLink } from "common/routing";
import {
  Attachment,
  Attachments,
  isAttachableToMap,
  isPropertyAttachment,
} from "common/utils/customObjects";
import {
  CurrentUserQuery,
  useGetAccountDocumentTemplatesForMapQuery,
} from "../../../generated/graphql";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { Button } from "../../Common/Buttons/Button";
import { EmptyState, EmptyStateProps } from "../../Common/EmptyState";
import Modal from "../../Common/Modal";
import UploadDocumentsForm from "../../DocumentUploads/UploadDocumentsForm";
import { DropdownMenu } from "../../Inputs";
import { LayerContext } from "../../Maps/layers";
import { Title } from "./__styles__/List";
import { useGenerateFromTemplateModal } from "./useGenerateFromTemplateModal";
import { filterObjectDocumentTemplates, ListObject } from "./utils";

export interface Props {
  object: ListObject;
  children: Array<React.ReactNode>;
}

const List = ({ object, children }: Props) => {
  const { authorized, isPublic, account } = useContext(AuthContext);
  const { updateMap } = useContext(LayerContext);
  const history = useHistory();
  const { t } = useTranslation();

  const canUploadDocument = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "create",
  });

  const canGenerateDocument = authorized({
    resource: RESOURCE_NAME.GENERATED_DOCUMENT,
    permission: "create",
  });

  const canListDocumentTemplates = authorized({
    resource: RESOURCE_NAME.DOCUMENT_TEMPLATE,
    permission: "list",
  });

  const { data, loading } = useGetAccountDocumentTemplatesForMapQuery({
    skip: !canListDocumentTemplates,
  });

  const documentTemplates = filterObjectDocumentTemplates(
    object,
    data?.account?.documentTemplates,
  );

  const accountDocumentTypeFilter: (
    accountDocumentType: NonNullable<
      CurrentUserQuery["currentUser"]
    >["account"]["accountDocumentTypes"][number],
  ) => boolean = accountDocumentType => {
    if (object.objectType === OBJECT_TYPE.PROPERTY) {
      return accountDocumentType.attachments.some(attachment =>
        isPropertyAttachment(attachment as Attachment),
      );
    } else {
      return isAttachableToMap({
        attachments: accountDocumentType.attachments as Attachments,
        customMapId: object.customMapId,
      });
    }
  };

  const hasAccountDocumentTypes = account?.accountDocumentTypes.filter(
    accountDocumentTypeFilter,
  ).length;

  const [showUploadModal, hideUploadModal] = useModal(
    () => (
      <Modal onRequestClose={hideUploadModal}>
        <UploadDocumentsForm
          closeModal={() => {
            hideUploadModal();
          }}
          updateMap={updateMap}
          object={object}
          accountDocumentTypeFilter={accountDocumentTypeFilter}
        />
      </Modal>
    ),
    [object],
  );

  const onClickUpload = () => {
    track("Clicked upload from EC List", {
      propertyId: object.id,
      fullAddress: object.objectLabel,
    });
    showUploadModal();
  };

  const onClickGenerate = () => {
    track("Clicked 'Generate from template' button", {
      propertyId: object.id,
      fullAddress: object.objectLabel,
    });
    void showGenerateFromTemplateModal();
  };

  const [showGenerateFromTemplateModal] = useGenerateFromTemplateModal({
    object,
    updateMap,
  });

  const actions = [
    {
      label: "Upload file",
      onClick: onClickUpload,
      disabled: !canUploadDocument || !hasAccountDocumentTypes,
    },
    {
      label: "Generate from template",
      onClick: onClickGenerate,
      disabled: !canGenerateDocument,
    },
  ];

  const AddNewButton = ({ onPress }: { onPress: (_: PressEvent) => void }) => (
    <Button
      onPress={onPress}
      variant="primary"
      size="small"
      data-testid="addNewButton"
      iconEnd="chevron-down"
      title="Add new"
    />
  );

  const title =
    isPublic ? t("property-files-empty-state-header") : `No files uploaded`;

  let body, primaryButtonProps;
  if (isPublic) {
    body = t("property-files-empty-state-text");
    primaryButtonProps = {
      label: t("get-help"),
      onClick: () => history.push(buildLink("getHelp")),
    };
  } else if (hasAccountDocumentTypes) {
    body = `Add files to get started.`;
  } else {
    body = `This feature has not been configured on your account. Reach out to your customer support representative to learn more.`;
    primaryButtonProps = {
      label: "Contact us",
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
    };
  }
  const emptyStateProps: EmptyStateProps = {
    icon: "add-file",
    title,
    body,
    primaryButtonProps,
  };

  return (
    <div>
      <Title>
        {t("property-files-header")}
        {!isPublic &&
          !loading &&
          (documentTemplates.length > 0 ?
            <DropdownMenu actions={actions} customButton={AddNewButton} />
          : <Button
              size="small"
              variant="primary"
              onPress={onClickUpload}
              isDisabled={!canUploadDocument || !hasAccountDocumentTypes}
              data-testid="uploadButton"
              iconStart="plus"
              title="Upload"
            />)}
      </Title>
      {children.length ? children : <EmptyState {...emptyStateProps} />}
    </div>
  );
};

export default List;
